import * as React from "react";
import { Treemap, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/utils";
import { CHART_COLORS } from "@/lib/chart-colors";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart";
import type { PerformanceData, Asset, StockAsset, EtfAsset } from "@/schemas";
import { useBaseCurrency } from "@/hooks/use-base-currency";

// Helper to get country flag emoji from country name
const getCountryFlag = (countryName: string): string => {
  const countryToCode: Record<string, string> = {
    "United States": "US",
    USA: "US",
    "United Kingdom": "GB",
    UK: "GB",
    Canada: "CA",
    France: "FR",
    Germany: "DE",
    Italy: "IT",
    Spain: "ES",
    Netherlands: "NL",
    Belgium: "BE",
    Switzerland: "CH",
    Austria: "AT",
    Sweden: "SE",
    Norway: "NO",
    Denmark: "DK",
    Finland: "FI",
    Ireland: "IE",
    Portugal: "PT",
    Greece: "GR",
    Poland: "PL",
    "Czech Republic": "CZ",
    Hungary: "HU",
    Romania: "RO",
    Russia: "RU",
    China: "CN",
    Japan: "JP",
    "South Korea": "KR",
    Korea: "KR",
    India: "IN",
    Australia: "AU",
    "New Zealand": "NZ",
    Brazil: "BR",
    Mexico: "MX",
    Argentina: "AR",
    Chile: "CL",
    "South Africa": "ZA",
    Israel: "IL",
    Turkey: "TR",
    "Saudi Arabia": "SA",
    UAE: "AE",
    "United Arab Emirates": "AE",
    Singapore: "SG",
    "Hong Kong": "HK",
    Taiwan: "TW",
    Thailand: "TH",
    Indonesia: "ID",
    Malaysia: "MY",
    Philippines: "PH",
    Vietnam: "VN",
  };

  const code = countryToCode[countryName];
  if (!code) return "";

  // Convert country code to flag emoji
  const codePoints = code
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

interface CountryAllocationChartProps {
  performanceData: PerformanceData | null;
  assetsMap: Map<string, Asset>;
  loading?: boolean;
  className?: string;
}

export function CountryAllocationChart({
  performanceData,
  assetsMap,
  loading = false,
  className,
}: CountryAllocationChartProps) {
  const { code: currencyCode } = useBaseCurrency();

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatCompactCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  };

  const { chartData, chartConfig, totalValue, countryCount } =
    React.useMemo(() => {
      if (!performanceData) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          countryCount: 0,
        };
      }

      const assets = performanceData.assets_performance;
      const totalPerf = performanceData.total_performance;

      if (!assets || Object.keys(assets).length === 0) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          countryCount: 0,
        };
      }

      if (!totalPerf) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          countryCount: 0,
        };
      }

      // If assets are still loading, return empty data
      if (assetsMap.size === 0) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          countryCount: 0,
        };
      }

      // Group by country and calculate total for stocks/ETFs only
      const countryGroups: Record<string, number> = {};
      let geographicTotal = 0;

      Object.entries(assets).forEach(([assetId, assetPerf]) => {
        const asset = assetsMap.get(assetId);
        // Only include stocks and ETFs which have country information
        if (asset && (asset.type === "stock" || asset.type === "etf")) {
          const country = (asset as StockAsset | EtfAsset).country || "Unknown";
          const value = assetPerf.current_value || 0;
          countryGroups[country] = (countryGroups[country] || 0) + value;
          geographicTotal += value;
        }
      });

      if (geographicTotal <= 0) {
        return {
          chartData: [],
          chartConfig: {} as ChartConfig,
          totalValue: 0,
          countryCount: 0,
        };
      }

      const finalTotal = geographicTotal;

      const children: Array<{
        name: string;
        size: number;
        percentage: number;
        color: string;
        key: string;
      }> = [];
      const config: ChartConfig = {
        value: { label: "Value" },
      };

      Object.entries(countryGroups)
        .filter(([, value]) => value > 0)
        .sort(([, a], [, b]) => b - a)
        .forEach(([country, value], index) => {
          const color = CHART_COLORS[index % CHART_COLORS.length];
          children.push({
            name: country,
            size: value,
            percentage: (value / finalTotal) * 100,
            color,
            key: country,
          });
          config[country] = {
            label: country,
            color,
          };
        });

      return {
        chartData: children,
        chartConfig: config,
        totalValue: finalTotal,
        countryCount: children.length,
      };
    }, [performanceData, assetsMap]);

  if (loading) {
    return (
      <div
        className={cn(
          "flex items-center justify-center h-full w-full text-muted-foreground text-sm",
          className,
        )}
      >
        Loading...
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div
        className={cn(
          "flex items-center justify-center h-full w-full text-muted-foreground text-sm",
          className,
        )}
      >
        No geographic data
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full w-full", className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <span className="text-sm font-medium text-foreground">Repartition</span>
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-muted-foreground">
            {countryCount} countries
          </span>
          <span className="text-sm font-semibold tracking-tight">
            {formatCompactCurrency(totalValue)}
          </span>
        </div>
      </div>

      {/* Content: Treemap */}
      <div className="flex-1 min-h-0 w-full">
        <ChartContainer config={chartConfig} className="w-full h-full">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={chartData}
              dataKey="size"
              stroke="hsl(var(--background))"
              fill="hsl(var(--primary))"
              content={<CustomTreemapContent />}
              isAnimationActive={false}
            >
              <ChartTooltip
                content={<AllocationTooltip formatCurrency={formatCurrency} />}
              />
            </Treemap>
          </ResponsiveContainer>
        </ChartContainer>
      </div>
    </div>
  );
}

type TreemapContentProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  color?: string;
  percentage?: number;
  payload?: { percentage?: number };
};

const CustomTreemapContent = (props: TreemapContentProps) => {
  const { x = 0, y = 0, width = 0, height = 0, name = "", color = "" } = props;
  const percentage = props.percentage ?? props.payload?.percentage ?? 0;
  const flag = getCountryFlag(name);

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: "hsl(var(--background))",
          strokeWidth: 2,
        }}
      />
      {width > 30 && height > 30 && (
        <foreignObject x={x} y={y} width={width} height={height}>
          <div className="w-full h-full p-1 flex flex-col items-center justify-center text-center gap-1 overflow-hidden">
            <span
              className="text-xs font-semibold text-white leading-tight line-clamp-2"
              title={name}
            >
              {flag && <span className="mr-0.5">{flag}</span>}
              {name}
            </span>
            {height > 45 && (
              <span className="text-xs text-white/90 font-mono font-semibold">
                {percentage.toFixed(0)}%
              </span>
            )}
          </div>
        </foreignObject>
      )}
    </g>
  );
};

const AllocationTooltip = ({
  active,
  payload,
  formatCurrency,
}: {
  active?: boolean;
  payload?: Array<{
    payload?: { name?: string; size?: number; percentage?: number };
    name?: string;
    value?: number;
  }>;
  formatCurrency: (value: number) => string;
}) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0]?.payload;
  if (!entry) return null;

  const name = entry.name ?? payload[0]?.name ?? "";
  const value = typeof entry.size === "number" ? entry.size : payload[0]?.value;
  const percentage =
    typeof entry.percentage === "number" ? entry.percentage : 0;
  const flag = getCountryFlag(name);

  return (
    <div className="rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-sm shadow-xl">
      <div className="flex flex-col gap-1 min-w-[180px]">
        <span className="text-sm font-semibold">
          {flag && <span className="mr-1">{flag}</span>}
          {name}
        </span>
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-sm">
            {formatCurrency(value as number)}
          </span>
          <span className="text-muted-foreground text-sm">
            {percentage.toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};
